import React, { ChangeEvent, useCallback } from "react";
import debounce from "lodash.debounce";
import MaskedInput from "react-text-mask";
import { useTranslation } from "react-i18next";
import { TradeIn } from "Types/TradeIn";
import { parseCurrencyToFloat } from "src/utils";
import createNumberMask from "text-mask-addons/dist/createNumberMask";
import { isUS } from "src/utils/account";

export const ManualEstimateInput = ({
    name,
    type = "text",
    uuid,
    country,
    onChangePatchTradePending,
    tradePending,
}: {
    name: string;
    type?: string;
    onChangePatchTradePending: (tradePending: TradeIn, uuid: string) => void;
    uuid: string;
    country: string;
    tradePending: Partial<TradeIn>;
}) => {
    const { t } = useTranslation();

    const handleChange = (event: ChangeEvent<HTMLInputElement>): void => {
        event.persist();
        const manualTradeField = event.currentTarget.name;
        const currentValue = event.currentTarget.value;
        debouncedHandleChange(manualTradeField, currentValue);
    };

    const debouncedHandleChange = useCallback(
        debounce((formFieldName: string, formFieldValue: any) => {
            if (formFieldName === "customer_estimate") {
                const tradeEstimateAmount = parseCurrencyToFloat(formFieldValue);
                onChangePatchTradePending({ [formFieldName]: tradeEstimateAmount }, uuid);
            } else {
                onChangePatchTradePending({ [formFieldName]: formFieldValue }, uuid);
            }
        }, 500),
        []
    );

    const currencyMaskOptions = {
        prefix: `${isUS(country) ? "$" : "CA$"}`,
        includeThousandsSeparator: true,
        thousandsSeparatorSymbol: ",",
        allowDecimal: true,
        decimalSymbol: ".",
        decimalLimit: 2,
        integerLimit: 7,
        allowNegative: false,
        allowLeadingZeroes: false,
    };
    const currencyMask = createNumberMask(currencyMaskOptions);

    return (
        <div key={name} className="manual-estimate-form-field">
            <label htmlFor={`${name}-input`} className="manual-estimate-form-label">
                {name === "customer_estimate"
                    ? `${t("MyGarage.GlobalPaymentsControl.TradePendingRow.header")}`
                    : `${t(`TradePending.ManualInputs.${name}`)}`}
            </label>
            <MaskedInput
                className="manual-estimate-form-input"
                id={`${name}-trade-input`}
                name={name}
                type={type}
                onChange={handleChange}
                defaultValue={tradePending[name] ?? ""}
                mask={
                    name === "customer_estimate" ? currencyMask : name === "year" ? [/[1-9]/, /\d/, /\d/, /\d/] : false
                }
                guide={false}
            />
        </div>
    );
};
